import { useMemo } from 'react'

import type { ChartData } from 'chart.js'
import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'
import { isDateRange } from 'react-day-picker'
import { useQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { dateToString } from '@/utils/date-converter.ts'
import { handleError } from '@/utils/error'

import { getSocialAccountsByDate } from '../data/service'
import type {
  SocialAccount,
  SocialAccountResponse,
  SocialMediaType,
} from '../types'

interface UseSocialDataProps {
  dateRange: DateRange | undefined
  socialMediaType: SocialMediaType
}

export interface SocialChartData {
  views: ChartData<'doughnut', number[], unknown>
  followers: ChartData<'doughnut', number[], unknown>
}

export interface SocialSummarizedData {
  views: number
  followers: number
  likes: number
  shares: number
  comments: number
  totalUploads: number
}

export interface SocialData {
  accounts: SocialAccountResponse | undefined
  isFetching: boolean
  chartData: SocialChartData | undefined
  summarizedData: SocialSummarizedData | undefined
  refetch: () => void
}

const CHART_COLORS = ['#0BCDC1', '#EF358E', '#9601CA', '#1E84E3']

export const useSocialData = ({
  dateRange,
  socialMediaType,
}: UseSocialDataProps): SocialData => {
  const creatorId = useUnit($creatorId)
  const {
    data: accounts,
    isFetching,
    isIdle,
    refetch,
  } = useQuery(
    ['socialAccountRanges', socialMediaType, creatorId, dateRange],
    () => {
      if (!creatorId || !isDateRange(dateRange)) {
        return Promise.resolve({
          views: [],
          links: [],
          'performance-states': {},
        })
      }

      const fromDate = dateToString(dateRange.from)
      const toDate = dateToString(dateRange.to)

      return getSocialAccountsByDate(
        creatorId,
        socialMediaType,
        fromDate,
        toDate,
      )
    },
    {
      enabled: !!creatorId,
      select: _transformSocialAccountResponse,
      onError: (error) =>
        handleError(error, 'Failed to get social accounts', refetch),
    },
  )

  const chartData: SocialChartData | undefined = useMemo(() => {
    if (!accounts || accounts.views.length === 0) return

    const names = accounts.views.map((account) => account.account)

    const viewsData = accounts.views.map((account) => account.viewsGrowth)
    const followersData = accounts.views.map(
      (account) => account.followersGrowth,
    )

    const viewsChart = _createDoughnutChart(names, viewsData)
    const followersChart = _createDoughnutChart(names, followersData)

    return {
      views: viewsChart,
      followers: followersChart,
    }
  }, [accounts])

  const summarizedData: SocialSummarizedData | undefined = useMemo(() => {
    if (!accounts || accounts.views.length === 0) return

    let views = 0
    let followers = 0
    let likes = 0
    let shares = 0
    let comments = 0

    for (const item of accounts.views) {
      views += item.viewsGrowth
      followers += item.followersGrowth
      likes += item.likesGrowth
      shares += item.sharesGrowth
      comments += item.commentsGrowth
    }

    return {
      views,
      followers,
      likes,
      shares,
      comments,
      totalUploads: accounts.totalUploads ?? 0,
    }
  }, [accounts])

  return useMemo(
    () => ({
      accounts,
      isFetching: isFetching || isIdle,
      chartData,
      summarizedData,
      refetch,
    }),
    [accounts, isFetching, isIdle, chartData, summarizedData, refetch],
  )
}

const _createDoughnutChart = (
  labels: Array<string>,
  data: Array<number>,
): ChartData<'doughnut'> => {
  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: CHART_COLORS,
      },
    ],
  }
}

const _transformSocialAccountResponse = (
  data: SocialAccountResponse,
): SocialAccountResponse => {
  if (!data) {
    return { views: [], links: [], 'performance-states': {} }
  }

  const aggregateAccountGrowth = (
    accumulator: { [account: string]: SocialAccount },
    {
      account,
      viewsGrowth,
      followersGrowth,
      sharesGrowth,
      likesGrowth,
      commentsGrowth,
      ...rest
    }: SocialAccount,
  ) => {
    const currentAccount = accumulator[account] || {
      viewsGrowth: 0,
      followersGrowth: 0,
      sharesGrowth: 0,
      likesGrowth: 0,
      commentsGrowth: 0,
      account,
      ...rest,
    }

    accumulator[account] = {
      ...currentAccount,
      viewsGrowth: currentAccount.viewsGrowth + viewsGrowth,
      followersGrowth: currentAccount.followersGrowth + followersGrowth,
      sharesGrowth: currentAccount.sharesGrowth + sharesGrowth,
      likesGrowth: currentAccount.likesGrowth + likesGrowth,
      commentsGrowth: currentAccount.commentsGrowth + commentsGrowth,
    }

    return accumulator
  }

  const totalGrowthByAccount = data.views.reduce(aggregateAccountGrowth, {})

  const sortedViews = Object.values(totalGrowthByAccount).sort((a, b) => {
    return b.viewsGrowth - a.viewsGrowth
  })

  return {
    views: sortedViews,
    links: data.links || [],
    'performance-states': data['performance-states'] ?? {},
    totalUploads: data.totalUploads ?? 0,
  }
}
